import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Music, Edit2 } from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const DanceStylesManagement = ({ user, onLogout }) => {
  const [styles, setStyles] = useState([]);
  const [tenures, setTenures] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openStyle, setOpenStyle] = useState(false);
  const [openTenure, setOpenTenure] = useState(false);
  const [openPricing, setOpenPricing] = useState(false);
  const [editModeStyle, setEditModeStyle] = useState(false);
  const [editingStyle, setEditingStyle] = useState(null);
  const [editModeTenure, setEditModeTenure] = useState(false);
  const [editingTenure, setEditingTenure] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);
  
  const [styleForm, setStyleForm] = useState({ name: "", levels: ["Beginner", "Intermediate", "Advanced"] });
  const [tenureForm, setTenureForm] = useState({ name: "", duration_months: "" });
  const [pricingForm, setPricingForm] = useState({ style_id: "", tenure_id: "", price: "" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [stylesRes, tenuresRes, pricingRes] = await Promise.all([
        api.get("/dance-styles"),
        api.get("/tenures"),
        api.get("/pricing"),
      ]);
      setStyles(stylesRes.data.styles);
      setTenures(tenuresRes.data.tenures);
      setPricing(pricingRes.data.pricing);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const createStyle = async () => {
    if (!styleForm.name) {
      toast.error("Please enter style name");
      return;
    }

    try {
      if (editModeStyle && editingStyle) {
        await api.put(`/dance-styles/${editingStyle.style_id}`, styleForm);
        toast.success("Dance style updated successfully!");
      } else {
        await api.post("/dance-styles", styleForm);
        toast.success("Dance style created successfully!");
      }
      
      fetchData();
      setOpenStyle(false);
      setEditModeStyle(false);
      setEditingStyle(null);
      setStyleForm({ name: "", levels: ["Beginner", "Intermediate", "Advanced"] });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save style");
    }
  };

  const startEditStyle = (style) => {
    setEditModeStyle(true);
    setEditingStyle(style);
    setStyleForm({ name: style.name, levels: style.levels });
    setOpenStyle(true);
  };

  const createTenure = async () => {
    if (!tenureForm.name || !tenureForm.duration_months) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      if (editModeTenure && editingTenure) {
        await api.put(`/tenures/${editingTenure.tenure_id}`, { ...tenureForm, duration_months: parseInt(tenureForm.duration_months) });
        toast.success("Tenure updated successfully!");
      } else {
        await api.post("/tenures", { ...tenureForm, duration_months: parseInt(tenureForm.duration_months) });
        toast.success("Tenure created successfully!");
      }
      
      fetchData();
      setOpenTenure(false);
      setEditModeTenure(false);
      setEditingTenure(null);
      setTenureForm({ name: "", duration_months: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save tenure");
    }
  };

  const startEditTenure = (tenure) => {
    setEditModeTenure(true);
    setEditingTenure(tenure);
    setTenureForm({ name: tenure.name, duration_months: tenure.duration_months.toString() });
    setOpenTenure(true);
  };

  const createPricing = async () => {
    if (!pricingForm.style_id || !pricingForm.tenure_id || !pricingForm.price) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      if (editingPricing) {
        await api.put(`/pricing/${editingPricing.pricing_id}`, { ...pricingForm, price: parseFloat(pricingForm.price) });
        toast.success("Pricing updated! Changes will apply to future renewals.");
      } else {
        await api.post("/pricing", { ...pricingForm, price: parseFloat(pricingForm.price) });
        toast.success("Pricing created successfully!");
      }
      
      fetchData();
      setOpenPricing(false);
      setEditingPricing(null);
      setPricingForm({ style_id: "", tenure_id: "", price: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save pricing");
    }
  };

  const startEditPricing = (priceItem) => {
    setEditingPricing(priceItem);
    setPricingForm({
      style_id: priceItem.style_id,
      tenure_id: priceItem.tenure_id,
      price: priceItem.price.toString(),
    });
    setOpenPricing(true);
  };

  const getPricingMatrix = () => {
    const matrix = {};
    pricing.forEach((p) => {
      const key = `${p.style_id}_${p.tenure_id}`;
      matrix[key] = p;
    });
    return matrix;
  };

  const pricingMatrix = getPricingMatrix();

  const getPricingItem = (styleId, tenureId) => {
    const key = `${styleId}_${tenureId}`;
    return pricingMatrix[key];
  };

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="p-4 md:p-8 pb-20 md:pb-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold font-heading text-slate-900" data-testid="dance-styles-title">
            Dance Styles & Pricing
          </h1>
          <p className="text-sm text-slate-600 mt-1">Manage dance styles, tenures, and pricing matrix</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Dance Styles Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold font-heading text-slate-900">Dance Styles</h2>
                <Dialog open={openStyle} onOpenChange={(isOpen) => {
                  setOpenStyle(isOpen);
                  if (!isOpen) {
                    setEditModeStyle(false);
                    setEditingStyle(null);
                    setStyleForm({ name: "", levels: ["Beginner", "Intermediate", "Advanced"] });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-style-button" className="h-10 rounded-full bg-primary hover:bg-primary/90 font-medium">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Style
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl">
                        {editModeStyle ? "Edit Dance Style" : "Create Dance Style"}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="style-name">Style Name</Label>
                        <Input
                          id="style-name"
                          data-testid="style-name-input"
                          placeholder="e.g., Hip Hop, Bharatnatyam"
                          value={styleForm.name}
                          onChange={(e) => setStyleForm({ ...styleForm, name: e.target.value })}
                          className="h-12 rounded-xl"
                        />
                      </div>
                      <Button
                        data-testid="create-style-button"
                        onClick={createStyle}
                        className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-medium"
                      >
                        {editModeStyle ? "Update Style" : "Create Style"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {styles.map((style, index) => (
                  <div
                    key={style.style_id}
                    data-testid={`style-card-${index}`}
                    className="p-4 bg-gradient-to-br from-violet-50 to-pink-50 rounded-xl border border-violet-100"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Music className="w-5 h-5 text-primary" />
                      <span className="font-medium text-slate-900">{style.name}</span>
                    </div>
                    <Button
                      data-testid={`edit-style-${index}`}
                      onClick={() => startEditStyle(style)}
                      size="sm"
                      variant="outline"
                      className="w-full rounded-full mt-2"
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Tenures Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold font-heading text-slate-900">Tenures</h2>
                <Dialog open={openTenure} onOpenChange={(isOpen) => {
                  setOpenTenure(isOpen);
                  if (!isOpen) {
                    setEditModeTenure(false);
                    setEditingTenure(null);
                    setTenureForm({ name: "", duration_months: "" });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-tenure-button" className="h-10 rounded-full bg-secondary hover:bg-secondary/90 font-medium">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Tenure
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl">
                        {editModeTenure ? "Edit Tenure" : "Create Tenure"}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="tenure-name">Tenure Name</Label>
                        <Input
                          id="tenure-name"
                          data-testid="tenure-name-input"
                          placeholder="e.g., Monthly, Quarterly"
                          value={tenureForm.name}
                          onChange={(e) => setTenureForm({ ...tenureForm, name: e.target.value })}
                          className="h-12 rounded-xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tenure-duration">Duration (Months)</Label>
                        <Input
                          id="tenure-duration"
                          data-testid="tenure-duration-input"
                          type="number"
                          placeholder="e.g., 1, 3, 6"
                          value={tenureForm.duration_months}
                          onChange={(e) => setTenureForm({ ...tenureForm, duration_months: e.target.value })}
                          className="h-12 rounded-xl"
                        />
                      </div>
                      <Button
                        data-testid="create-tenure-button"
                        onClick={createTenure}
                        className="w-full h-12 rounded-full bg-secondary hover:bg-secondary/90 font-medium"
                      >
                        {editModeTenure ? "Update Tenure" : "Create Tenure"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {tenures.map((tenure, index) => (
                  <div
                    key={tenure.tenure_id}
                    data-testid={`tenure-card-${index}`}
                    className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-100"
                  >
                    <p className="font-medium text-slate-900">{tenure.name}</p>
                    <p className="text-sm text-slate-600 mt-1">{tenure.duration_months} months</p>
                    <Button
                      data-testid={`edit-tenure-${index}`}
                      onClick={() => startEditTenure(tenure)}
                      size="sm"
                      variant="outline"
                      className="w-full rounded-full mt-2"
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Matrix Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold font-heading text-slate-900">Pricing Matrix</h2>
                <Dialog open={openPricing} onOpenChange={(isOpen) => {
                  setOpenPricing(isOpen);
                  if (!isOpen) {
                    setEditingPricing(null);
                    setPricingForm({ style_id: "", tenure_id: "", price: "" });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-pricing-button" className="h-10 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:opacity-90 font-medium">
                      <Plus className="w-4 h-4 mr-2" />
                      Set Price
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl">
                        {editingPricing ? "Edit Pricing" : "Set Pricing"}
                      </DialogTitle>
                      {editingPricing && (
                        <p className="text-sm text-slate-600 mt-2">Changes will apply to future subscription renewals</p>
                      )}
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="pricing-style">Dance Style</Label>
                        <select
                          id="pricing-style"
                          data-testid="pricing-style-select"
                          value={pricingForm.style_id}
                          onChange={(e) => setPricingForm({ ...pricingForm, style_id: e.target.value })}
                          className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white"
                        >
                          <option value="">Select Style</option>
                          {styles.map((style) => (
                            <option key={style.style_id} value={style.style_id}>
                              {style.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pricing-tenure">Tenure</Label>
                        <select
                          id="pricing-tenure"
                          data-testid="pricing-tenure-select"
                          value={pricingForm.tenure_id}
                          onChange={(e) => setPricingForm({ ...pricingForm, tenure_id: e.target.value })}
                          className="w-full h-12 rounded-xl border border-slate-200 px-4 bg-white"
                        >
                          <option value="">Select Tenure</option>
                          {tenures.map((tenure) => (
                            <option key={tenure.tenure_id} value={tenure.tenure_id}>
                              {tenure.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pricing-price">Price (₹)</Label>
                        <Input
                          id="pricing-price"
                          data-testid="pricing-price-input"
                          type="number"
                          placeholder="e.g., 5000"
                          value={pricingForm.price}
                          onChange={(e) => setPricingForm({ ...pricingForm, price: e.target.value })}
                          className="h-12 rounded-xl"
                        />
                      </div>
                      <Button
                        data-testid="create-pricing-button"
                        onClick={createPricing}
                        className="w-full h-12 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:opacity-90 font-medium"
                      >
                        {editingPricing ? "Update Price" : "Set Price"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {styles.length > 0 && tenures.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-heading text-slate-900">Dance Style</th>
                        {tenures.map((tenure) => (
                          <th key={tenure.tenure_id} className="text-center p-3 font-heading text-slate-900">
                            {tenure.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {styles.map((style) => (
                        <tr key={style.style_id} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-medium text-slate-900">{style.name}</td>
                          {tenures.map((tenure) => {
                            const priceItem = getPricingItem(style.style_id, tenure.tenure_id);
                            return (
                              <td key={tenure.tenure_id} className="text-center p-3">
                                {priceItem ? (
                                  <div className="flex items-center justify-center gap-2">
                                    <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                      ₹{priceItem.price}
                                    </span>
                                    <Button
                                      data-testid={`edit-price-${style.style_id}-${tenure.tenure_id}`}
                                      onClick={() => startEditPricing(priceItem)}
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    data-testid={`set-price-${style.style_id}-${tenure.tenure_id}`}
                                    onClick={() => {
                                      setPricingForm({ style_id: style.style_id, tenure_id: tenure.tenure_id, price: "" });
                                      setOpenPricing(true);
                                    }}
                                    size="sm"
                                    variant="ghost"
                                    className="text-slate-400 text-sm"
                                  >
                                    <Plus className="w-3 h-3 mr-1" />
                                    Set
                                  </Button>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-slate-600 py-8">Create styles and tenures first to set pricing</p>
              )}
            </div>
          </div>
        )}
      </div>

      <BottomNav user={user} onLogout={onLogout} />
    </div>
  );
};

export default DanceStylesManagement;
